import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Box, Skeleton, Stack } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { useQuery } from "@tanstack/react-query"
import { isEmpty } from "lodash"
import { FormProvider, useForm } from "react-hook-form"

import { useUrlParams } from "@ggs/hooks"
import { addNode, removeNode, useDispatch, useSelector } from "@ggs/store"
import { Container, FC, Pagination, SearchFacets, Text, TripCollection, TripSearchForm } from ".."
import { mapTripTeaser, mapWishlistData } from "../../helpers/data-mappers"
import { useTripsSearchData } from "../../local/hooks"
import { useGeoSearchData } from "../../local/hooks/useGeoSearchData"
import {
  formatSearchData,
  getIdFromString,
  getLabelFromString,
  getRangeFromString,
  getSortOption,
  setFormFields,
  getCountryNameById,
  getDestinationNameById,
} from "./helpers"

type TripGeneralSearchProps = {
  isSpecialsSearch?: boolean
  data?: any
  slug?: string
}

const TripGeneralSearch: FC<TripGeneralSearchProps> = ({
  data = null,
  isSpecialsSearch = false,
  slug = "/trips",
}) => {
  const dispatch = useDispatch()
  const wishlist = useSelector((state) => state.views.wishlist)
  const likedTrips = wishlist?.trip && wishlist?.trip.map((trip: any) => trip.nid)
  const { getTripsSolrData, getUserPriceSortField } = useTripsSearchData()
  const { allGeoOptions } = useGeoSearchData()
  const listRef = useRef<HTMLDivElement>(null)
  const userCountry = useSelector((state) => state.settings.country)
  const currentCountry = useRef(userCountry?.code || "")

  type SearchParams = NonNullable<Parameters<typeof getTripsSolrData>[number]>
  type SearchResults = Awaited<ReturnType<typeof getTripsSolrData>>

  const methods = useForm({
    defaultValues: {
      destination: null,
      country: null,
      experiences: [],
      budget: [],
      duration: [],
    },
  })

  const { reset, setValue, getValues } = methods

  const { params, setParam, resetParams } = useUrlParams(
    {
      destination: null,
      country: null,
      location: null,
      tripStyles: null,
      tripTypes: null,
      budget: null,
      duration: null,
      page: null,
      sort: null,
      specials: null,
    },
    slug
  )

  const PAGE_SIZE = 9
  const [tripSearchResults, setTripSearchResults] = useState<SearchResults | null>(data)

  const composeSearchStateFromQueryParams = (params: any) => {
    return {
      ...(params.destination && { destination: params.destination as string }),
      ...(params.country && { country: params.country as string }),
      ...(params.location && { location: decodeURIComponent(params.location as string) }),
      ...(params.tripStyles && { tripStyles: getIdFromString(params.tripStyles as string) }),
      ...(params.tripTypes && { tripTypes: getIdFromString(params.tripTypes as string) }),
      ...(params.duration && { duration: getRangeFromString(params.duration as string) }),
      ...(params.budget && { budget: getRangeFromString(params.budget as string) }),
      ...(params.page && {
        pageIndex: Math.max((parseInt(params.page as string) - 1) * PAGE_SIZE, 0),
      }),
      pageSize: PAGE_SIZE,
    }
  }

  const currentSearchState = useMemo(() => composeSearchStateFromQueryParams(params), [params])
  const [searchParams, setSearchParams] = useState<any>(currentSearchState)

  const setUrlParam = (value: any, type: string) => {
    if (value) {
      if (type === "experiences") {
        const tripTypes: string[] = []
        const tripStyles: string[] = []
        value.forEach((filter: any) => {
          const labelId = [filter.label, filter.value].join(":")
          filter?.experience === "Trip Types" ? tripTypes.push(labelId) : tripStyles.push(labelId)
        })
        if (tripTypes.length > 0) setParam("tripTypes", tripTypes.join(","))
        else setParam("tripTypes", null)
        if (tripStyles.length > 0) setParam("tripStyles", tripStyles.join(","))
        else setParam("tripStyles", null)
      } else if (["budget", "duration"].includes(type)) {
        const range = value.join("-")
        setParam(type, range)
      } else {
        setParam(type, value.value)
      }
    } else {
      setParam(type, null)
    }
    setParam("page", 1)
  }

  const handleApplyFilter = (filter: any, type: string) => {
    // Set new url params
    setUrlParam(filter, type)
    // Apply new filtering
    if (type === "experiences") {
      const tripStyles: string[] = []
      const tripTypes: string[] = []
      filter.forEach((item: any) => {
        if (item?.experience === "Trip Styles") tripStyles.push(item?.value)
        else tripTypes.push(item?.value)
      })
      setSearchParams((params: any) => ({
        ...params,
        tripStyles: tripStyles.join(","),
        tripTypes: tripTypes.join(","),
      }))
    } else if (["budget", "duration"].includes(type)) {
      setSearchParams((params: any) => ({
        ...params,
        pageIndex: 0,
        [type]: {
          min: filter[0] || null,
          max: filter[1] || null,
        },
      }))
    } else {
      setSearchParams((params: any) => ({ ...params, pageIndex: 0, [type]: filter?.value || null }))
    }
  }

  const onPageChange = useCallback((page: number) => {
    const pageIndex = Math.max((page - 1) * PAGE_SIZE, 0)
    setParam("page", `${page}`)
    setSearchParams((params: any) => ({
      ...params,
      pageIndex,
    }))
    // scroll to top of results
    if (listRef.current) {
      const listItemRect = listRef.current.getBoundingClientRect()
      const topOffset = window.scrollY + listItemRect.top - 150
      window.scrollTo({ top: topOffset, behavior: "smooth" })
    }
  }, [])

  const onSortChange = useCallback(
    (type: string) => {
      const [fieldKey, fieldDirection] = type.split("-")
      let sortField = fieldKey

      if (fieldKey === "price") {
        sortField = getUserPriceSortField()
      }

      setParam("sort", `${sortField}-${fieldDirection}`)
      setSearchParams((params: any) => ({
        ...params,
        sortField: sortField,
        sortDirection: fieldDirection,
      }))
    },
    [userCountry]
  )

  const onSpecialsToggle = useCallback((value: boolean) => {
    // Convert boolean to string to match URL param handling
    setParam("specials", value ? "true" : null)
    setSearchParams((params: any) => ({
      ...params,
      pageIndex: 0,
      specials: Boolean(value),
    }))
  }, [])

  const removeAllFilters = useCallback(() => {
    const defaultValues = {
      destination: null,
      country: null,
      tripStyles: null,
      tripTypes: null,
      budget: { min: null, max: null },
      duration: { min: null, max: null },
      pageIndex: 0,
      specials: false,
    }
    setSearchParams((params: any) => ({ ...params, ...defaultValues }))
    // reset URL query params
    resetParams({ page: params.page })
    // reset form fields
    reset()
    // change to first page
    setParam("page", 1)
  }, [])

  const usedFilters = useMemo(() => {
    // Create fallback data from geo search when Solr facets are empty
    const fallbackCountries = allGeoOptions
      ?.filter((item: any) => item.type === 'country')
      ?.map((item: any) => ({ label: item.label, value: item.external_id.toString() })) || []
    
    const fallbackDestinations = allGeoOptions
      ?.filter((item: any) => item.type === 'destination')
      ?.map((item: any) => ({ label: item.label, value: item.external_id.toString() })) || []

    // Use Solr facets if available, otherwise use fallback data
    const countriesData = tripSearchResults?.countries?.length > 0 ? tripSearchResults.countries : fallbackCountries
    const destinationsData = tripSearchResults?.destinations?.length > 0 ? tripSearchResults.destinations : fallbackDestinations

    const facets = []
    if (params?.destination) {
      const destinationName = getDestinationNameById(params?.destination as string, destinationsData)
      facets.push({ label: destinationName, type: "destination" })
    }

    if (params?.country) {
      const countryName = getCountryNameById(params?.country as string, countriesData)
      facets.push({ label: countryName, type: "country" })
    }

    if (params?.location) {
      facets.push({ label: getLabelFromString(params?.location as string), type: "location" })
    }

    if (params?.tripStyles) {
      const styles = getLabelFromString(params?.tripStyles as string).split(",")
      styles?.forEach((style: string) =>
        facets.push({ label: style, type: "experience", experienceType: "tripStyles" })
      )
    }

    if (params?.tripTypes) {
      const types = getLabelFromString(params?.tripTypes as string).split(",")
      types?.forEach((type: string) =>
        facets.push({ label: type, type: "experience", experienceType: "tripTypes" })
      )
    }

    if (params?.budget) {
      const budget = getLabelFromString(params?.budget as string).split("-")
      facets.push({ label: budget.join(" - "), type: "budget" })
    }

    if (params?.duration) {
      const duration = getLabelFromString(params?.duration as string).split("-")
      facets.push({ label: duration.join(" - "), type: "duration" })
    }

    return facets
  }, [params, tripSearchResults?.countries, tripSearchResults?.destinations, allGeoOptions])

  const removeIndividualFilter = (facet: any) => {
    if (facet.type === "experience") {
      const { experiences } = getValues()
      setValue(
        "experiences",
        experiences.filter((item: any) => item.label !== facet.label)
      )

      if (facet?.experienceType === "tripStyles") {
        const newParam = params?.tripStyles
          //@ts-ignore
          ?.split(",")
          ?.filter((value: string) => facet.label !== value.split(":")[0])

        setParam("tripStyles", newParam.join(","))
      }

      if (facet?.experienceType === "tripTypes") {
        const newParam = params?.tripTypes
          //@ts-ignore
          ?.split(",")
          ?.filter((value: string) => facet.label !== value.split(":")[0])

        setParam("tripTypes", newParam.join(","))
      }
    } else {
      setValue(facet?.type, null)
      setParam(facet?.type, null)
    }

    const newSearchParams = formatSearchData(getValues() as any)
    const newSearchState = {
      ...params,
      ...newSearchParams,
      pageIndex: 0,
      pageSize: PAGE_SIZE,
    }
    setSearchParams((params: any) => ({ ...searchParams, ...newSearchState }))
  }

  const {
    status,
    isFetching,
    data: tripData,
    error,
  } = useQuery({
    queryKey: ["tripSearch", { searchParams, userCountry }],
    queryFn: async () => {
      const data = await getTripsSolrData({
        ...searchParams,
        ...(isSpecialsSearch && { specials: true }),
      })
      const filteredData = mapWishlistData(likedTrips, data.results)
      const results = { ...data, results: filteredData }
      setTripSearchResults(results)
      setFirstLoaded(true)
      return results
    },
    initialData: data,
  })
  const [firstLoaded, setFirstLoaded] = useState(!!data)

  // Reset if country change, ensure price and sort fields don't stick to previous country currency.
  useEffect(() => {
    if (currentCountry.current !== "" && currentCountry.current !== userCountry?.code) {
      removeAllFilters()
      currentCountry.current = userCountry?.code
    }
  }, [userCountry])

  useEffect(() => {
    // Create fallback data from geo search when Solr facets are empty
    const fallbackCountries = allGeoOptions
      ?.filter((item: any) => item.type === 'country')
      ?.map((item: any) => ({ label: item.label, value: item.external_id.toString() })) || []
    
    const fallbackDestinations = allGeoOptions
      ?.filter((item: any) => item.type === 'destination')
      ?.map((item: any) => ({ label: item.label, value: item.external_id.toString() })) || []

    // Use Solr facets if available, otherwise use fallback data
    const countriesData = tripSearchResults?.countries?.length > 0 ? tripSearchResults.countries : fallbackCountries
    const destinationsData = tripSearchResults?.destinations?.length > 0 ? tripSearchResults.destinations : fallbackDestinations

    const fields: any = setFormFields(params, countriesData, destinationsData)
    Object.keys(fields).forEach((field: any) => {
      if (["budget", "duration"].includes(field)) {
        setValue(field, fields[field]?.value)
      } else setValue(field, fields[field])
    })
    // set sort field if it exists in query params when page refreshes
    if (params.sort) onSortChange(params.sort as string)
    // set specials checkbox if it exists in query params when page refreshes
    // Only call onSpecialsToggle if the current state doesn't match the URL param
    const urlSpecialsValue = params.specials === "true"
    if (params.specials && !isSpecialsSearch && searchParams.specials !== urlSpecialsValue) {
      onSpecialsToggle(urlSpecialsValue)
    }
  }, [tripSearchResults?.countries, tripSearchResults?.destinations, allGeoOptions])

  return (
    <Container maxWidth={1480}>
      <FormProvider {...methods}>
        <Box
          sx={(theme) => ({
            borderRadius: 1.5,
            boxShadow: `0px 2px 10px 0px ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
            transform: {
              xs: `translateY(${theme.spacing(-18)})`,
              md: isSpecialsSearch ? "none" : `translateY(${theme.spacing(-8.5)})`,
            },
            marginBottom: { xs: -8, md: isSpecialsSearch ? 0 : -8.5 },
            marginTop: { xs: 0, md: isSpecialsSearch ? 20 : 0 },
            position: "relative",
            zIndex: 10,
          })}
        >
          <TripSearchForm
            destinations={tripSearchResults?.destinations}
            countries={tripSearchResults?.countries}
            experiences={tripSearchResults?.experiences}
            prices={tripSearchResults?.prices}
            durations={tripSearchResults?.durations}
            onApplyFilter={handleApplyFilter}
            searchParams={searchParams}
          />
        </Box>
        {isFetching && !firstLoaded && (
          <>
            <Box pt={{ xs: 5, md: 10 }} pb={15}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Skeleton variant="rounded" width={80} height={16} sx={{ mb: 5 }} />
                <Stack direction="row" gap={2}>
                  <Skeleton variant="rounded" width={170} height={68} />
                  <Skeleton
                    variant="rounded"
                    width={170}
                    height={68}
                    sx={{ display: { xs: "none", md: "block" } }}
                  />
                </Stack>
              </Stack>
            </Box>
            <Box pb={{ xs: 19, md: 20 }}>
              <Box
                display="grid"
                gridTemplateColumns={{
                  xs: "minmax(0, 1fr)",
                  md: "repeat(2, minmax(0, 1fr))",
                  xl: "repeat(3, minmax(0, 1fr))",
                }}
                sx={{
                  columnGap: { xs: 4, md: 10 },
                  rowGap: { xs: 4, md: 10 },
                }}
              >
                <Skeleton variant="rounded" width="100%" height={682} />
                <Skeleton variant="rounded" width="100%" height={682} />
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={682}
                  sx={{ display: { xs: "none", md: "block" } }}
                />
              </Box>
            </Box>
          </>
        )}
        {status === "success" && firstLoaded && (
          <>
            <Box pt={5} pb={15}>
              <SearchFacets
                facets={usedFilters}
                sortOptions={[
                  { label: "Duration, Short to Long", type: "its_duration-asc" },
                  { label: "Duration, Long to Short", type: "its_duration-desc" },
                  { label: "Price, Low to High", type: "price-asc" },
                  { label: "Price, High to Low", type: "price-desc" },
                ]}
                count={tripSearchResults?.count}
                removeAllFilters={removeAllFilters}
                removeIndividualFilter={removeIndividualFilter}
                sortChange={onSortChange}
                paramSort={getSortOption(params.sort as string)}
                onSpecialsToggle={onSpecialsToggle}
                isSpecialsChecked={params.specials as boolean}
                showSpecialsToggle={!isSpecialsSearch}
              />
            </Box>
            <Box pb={{ xs: 19, md: 20 }} ref={listRef}>
              {tripSearchResults?.results !== null && isEmpty(tripSearchResults?.results) ? (
                <Text title="No results found" text="Please review your search criteria." />
              ) : (
                <TripCollection
                  items={tripSearchResults?.results || []}
                  showSpecialTag={Boolean(params.specials)}
                />
              )}
            </Box>
            {tripSearchResults?.totalPages > 1 && (
              <Box pb={{ xs: 15, md: 11 }}>
                <Pagination
                  page={parseInt(params.page as string) || 1}
                  count={tripSearchResults?.totalPages}
                  onPageChange={onPageChange}
                />
              </Box>
            )}
          </>
        )}
      </FormProvider>
    </Container>
  )
}

export default TripGeneralSearch
